// Fields from the raw piping_spec_<Spec> tables that should NOT be copied
// onto allCombinationTable rows. Every other column is copied through.
// Size is excluded because the row's Size is the *expanded* size value, while

import { Add } from "@mui/icons-material";
import { getPipingTable } from "../sqliteApi";
// the raw spec row's Size is the original range — overwriting would corrupt it.
export const EXCLUDED_SPEC_FIELDS = new Set([
  "clientName",
  "Rev",
  "SoftwareName",
  "Est_Table_Name",
  "Parts_Lib_Tab",
  "Description_String",
  "Table_Name",
  "Relative_Path",
  "Size",
]);

// A value is worth copying onto a row only if it's actually something —
// null, undefined, and empty/whitespace strings are treated as "no value".
export function hasMeaningfulValue(value) {
  if (value === undefined || value === null) return false;
  if (typeof value === "string" && value.trim() === "") return false;
  return true;
}

// Return a shallow copy of a spec row with EXCLUDED_SPEC_FIELDS dropped and
// every null/undefined/empty value stripped out. Used when seeding a new
// allCombinationTable row so excluded/empty columns don't leak via a spread.
export function filterSpecRow(row) {
  const out = {};
  if (!row) return out;
  for (const [field, value] of Object.entries(row)) {
    if (EXCLUDED_SPEC_FIELDS.has(field)) continue;
    if (!hasMeaningfulValue(value)) continue;
    out[field] = value;
  }
  return out;
}

// Look up the matching row in the raw (non-deduped) piping_spec_<Spec> table
// using the piping context's allTables object. Returns undefined if no match.
export async function findRawSpecRow( rowData) {
  const { Spec, Components, Size, OriginalSizeRange } = rowData || {};
  const specKey = Spec ? `piping_spec_${Spec}` : null;
  if (!specKey) return undefined;

  const rawSpecTable = await getPipingTable(specKey);
  if (!Array.isArray(rawSpecTable) || rawSpecTable.length === 0)
    return undefined;

  const searchSize = String(OriginalSizeRange || Size || "").trim();
  const searchComp = String(Components || "")
    .trim()
    .toLowerCase();

  return rawSpecTable.find((cur) => {
    if (
      String(cur?.Components || "")
        .trim()
        .toLowerCase() !== searchComp
    )
      return false;

    if (searchSize) {
      const dbSize = String(cur?.Size || "").trim();
      if (dbSize !== searchSize) return false;
    }

    if (
      rowData["Schedule/Rating"] &&
      cur["Schedule/Rating"] !== rowData["Schedule/Rating"]
    )
      return false;
    if (rowData.Material && cur.Material !== rowData.Material) return false;
    if (rowData.Ends && cur.Ends !== rowData.Ends) return false;
    if (rowData["Stock Code"] && cur["Stock Code"] !== rowData["Stock Code"])
      return false;

    return true;
  });
}

export async function updateDerivedFields(
  filteredSelectedSpecs,
  handleFieldUpdate,
  rowIndex,
  rowData,
) {
  const { Components, Size, Spec, OriginalSizeRange } = rowData;

  const baseMatch = (extra = {}) =>
    filteredSelectedSpecs.find(
      (cur) =>
        cur.Spec === Spec &&
        cur.Components === Components &&
        cur.Size === OriginalSizeRange &&
        cur["Schedule/Rating"] === rowData["Schedule/Rating"] &&
        cur["Stock Code"] === rowData["Stock Code"] &&
        (!rowData["Material"] || cur["Material"] === rowData["Material"]) &&
        (!rowData["Ends"] || cur["Ends"] === rowData["Ends"]) &&
        Object.entries(extra).every(([key, val]) => cur[key] === val),
    );

  const target = baseMatch();
  const rawSpecRow = await findRawSpecRow(rowData);

  // Prefer the raw (non-deduped) row; fall back to the deduped target when
  // a field is missing from the raw row.
  const pick = (field) => {
    const fromRaw = rawSpecRow?.[field];
    if (hasMeaningfulValue(fromRaw)) return fromRaw;
    const fromTarget = target?.[field];
    return hasMeaningfulValue(fromTarget) ? fromTarget : undefined;
  };

  // Copy every non-excluded, meaningful column (Schedule/Rating, Ends,
  // Dimensional Standard, Material, Additional Notes, Stock Code, Commodity
  // Code, Grizzly Code, and any other DB columns) onto the row in one pass.
  const allFields = new Set([
    ...Object.keys(target || {}),
    ...Object.keys(rawSpecRow || {}),
  ]);
  for (const field of allFields) {
    if (EXCLUDED_SPEC_FIELDS.has(field)) continue;
    const value = pick(field);
    if (value === undefined) continue;
    handleFieldUpdate(rowIndex, field, value);
  }

  const schedRating = pick("Schedule/Rating");
  const ends = pick("Ends");
  const material = pick("Material");
  const StockCode = pick("Stock Code");
  const AdditionalNotes = pick("Additional Notes");

  if (Components && OriginalSizeRange && material) {
    const desc = await generateDescriptionFromDB(
      Components,
      Size,
      schedRating,
      ends,
      material,
      Spec,
      StockCode,
      OriginalSizeRange,
      rowData.Description_String,
      AdditionalNotes,
    );
    handleFieldUpdate(rowIndex, "Material Description", desc);
  } else {
    handleFieldUpdate(rowIndex, "Material Description", "");
  }
}

export async function  generateDescriptionFromDB(
  Components,
  Size,
  Schedule,
  Ends,
  Material,
  Spec,
  StockCode,
  OriginalSizeRange,
  cachedDescriptionString,
  AdditionalNotes,
) {
  function interpolate(template) {
    let description = template;
    let actualSize = Size || "";
    let ND2 = "";
    if (Size && Size.includes(" X ")) {
      const sizeParts = Size.split(" X ").map((s) => s.trim());
      actualSize = sizeParts[0] || "";
      ND2 = sizeParts[1] || "";
    }

    if (
      AdditionalNotes !== null &&
      AdditionalNotes !== undefined &&
      AdditionalNotes !== "" &&
      AdditionalNotes !== "-"
    ) {
      const AdditionalNotesArray = AdditionalNotes.split(",").map((s) =>
        s.trim(),
      );

      const SNField = AdditionalNotesArray.find((note) =>
        note.toLowerCase().includes("sch"),
      );

      if (SNField) {
        // console.log("Additional Notes", AdditionalNotes);
        // console.log("SN Field found:", SNField);

        let SNValue = "";

        // Extract the value after "SCH" (case insensitive)
        const schMatch = SNField.match(/SCH\s+(\S+)/i);
        if (schMatch && schMatch[1]) {
          SNValue = schMatch[1].trim();
        }

        description = description.replace(/\{SN\}/gi, SNValue || "");
      }
    }

    description = description.replace(/\{component\}/gi, Components || "");
    description = description.replace(/\{size\}/gi, actualSize);
    description = description.replace(/\{nd2\}/gi, ND2);
    description = description.replace(/\{sch\}/gi, Schedule || "");
    description = description.replace(/\{schedule\}/gi, Schedule || "");
    description = description.replace(/\{Schedule\/Rating\}/gi, Schedule || "");
    description = description.replace(/\{sch\/rating\}/gi, Schedule || "");
    description = description.replace(/\{ends\}/gi, Ends || "");
    description = description.replace(/\{material\}/gi, Material || "");

    return description;
  }

  if (cachedDescriptionString != null) {
    const cached = String(cachedDescriptionString).trim();
    if (cached && cached !== "-") {
      return interpolate(cached);
    }
  }

  const specTableKey = Spec ? `piping_spec_${Spec}` : null;
  if (!specTableKey) {
    console.warn("Cannot generate description - missing Spec");
    return "";
  }

  const specTable = await getPipingTable(specTableKey);
  if (!specTable || !specTable.length) {
    console.warn(`Spec table not found: ${specTableKey}`);
    return "";
  }

  const searchSize = (OriginalSizeRange || Size || "").trim();
  const searchStockCode = StockCode ? String(StockCode).trim() : "";

  const specRow = specTable.find((cur) => {
    const componentMatch =
      (cur.Components || "").trim().toLowerCase() ===
      (Components || "").toLowerCase();
    if (!componentMatch) return false;

    if (cur.Size !== undefined && cur.Size !== null) {
      const dbSize = String(cur.Size || "").trim();
      if (searchSize && dbSize !== searchSize) return false;
    }

    if (cur["Stock Code"] !== undefined && cur["Stock Code"] !== null) {
      const dbStockCode = (cur["Stock Code"] || "").trim();
      if (searchStockCode && dbStockCode !== searchStockCode) return false;
    }

    return true;
  });

  if (!specRow) {
    console.warn(
      `[generateDescriptionFromDB] ❌ No row found for Component="${Components}", Size="${searchSize}", StockCode="${searchStockCode}"`,
    );
    return "";
  }

  const dt = (specRow.Description_String ?? "").toString().trim();
  if (!dt || dt === "-") {
    console.warn(
      `[generateDescriptionFromDB] ❌ DB Error: invalid Description_String for Component="${Components}", Size="${searchSize}", StockCode="${searchStockCode}"`,
    );
    return "DB Error";
  }

  return interpolate(dt);
}

export function resetDerivedFields(handleFieldUpdate, rowIndex) {
  handleFieldUpdate(rowIndex, "Schedule/Rating", "");
  handleFieldUpdate(rowIndex, "Ends", "");
  // handleFieldUpdate(rowIndex, "Dimensional Standard", "");
  handleFieldUpdate(rowIndex, "Material", "");
  handleFieldUpdate(rowIndex, "Additional Notes", "");
  handleFieldUpdate(rowIndex, "Material Description", "");
}

export async function getFamilyTableOptions(
  component,
  spec,
  currentRowData = null,
  fullSpecTable = null,
) {
  // Use fullSpecTable if provided, otherwise fall back to allTables
  let specData;
  if (fullSpecTable) {
    specData = fullSpecTable.filter((row) => row.Spec === spec);
  } else {
    const specTable = await getPipingTable(`piping_spec_${spec}`);
    specData = specTable || [];
  }

  if (!specData || !specData.length) return [];

  const result = specData
    .filter((cur) => {
      if (cur?.Components !== component) return false;

      if (currentRowData) {
        const schedRating = currentRowData["Schedule/Rating"];
        const ends = currentRowData["Ends"];
        const material = currentRowData["Material"];
        const addNotes = currentRowData["Additional Notes"];

        if (schedRating && cur["Schedule/Rating"] !== schedRating) return false;
        if (ends && cur["Ends"] !== ends) return false;
        if (material && cur["Material"] !== material) return false;
        if (
          addNotes &&
          addNotes !== "-" &&
          cur["Additional Notes"] !== addNotes
        )
          return false;
      }

      return true;
    })
    .map((cur) => ({
      Family_Name: cur?.Family_Name,
      Family_Table: cur?.Table_Name,
    }))
    .filter((item) => item.Family_Name && item.Family_Name !== "-");

  const seen = new Set();
  return result.filter((item) => {
    if (seen.has(item.Family_Name)) return false;
    seen.add(item.Family_Name);
    return true;
  });
}

export function transformComponentName(component) {
  if (!component) return null;

  let transformed = component.trim();
  transformed = transformed.replace(/\s+/g, "_");
  transformed = transformed.replace(/^_+|_+$/g, "");

  return transformed;
}
